import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Form, Table, Modal, Alert, Row, Col, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { SALES_CATEGORIES, TAKE_A_LOT_PROGRAM } from '../constants/salesCategories';
import TakeALotLeaderboard from '../components/TakeALotLeaderboard';

const TakeALotSales = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // State management
  const [salesHistory, setSalesHistory] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New submission form state
  const [newSubmission, setNewSubmission] = useState({
    salesEntries: [
      { category: '', itemName: '', amount: '' }
    ],
    notes: ''
  });

  // Redirect if not a learner
  useEffect(() => {
    if (userProfile && userProfile.role !== 'learner') {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // Load user's sales history on mount
  useEffect(() => {
    if (currentUser) {
      loadSalesHistory();
    }
  }, [currentUser]);

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [success]);

  /**
   * Load sales history from Firestore
   */
  const loadSalesHistory = async () => {
    try {
      setLoading(true);

      let snapshot = null;
      try {
        const orderedQuery = query(
          collection(db, 'sales'),
          where('userId', '==', currentUser.uid),
          orderBy('submittedAt', 'desc')
        );
        snapshot = await getDocs(orderedQuery);
      } catch (orderedError) {
        // Fallback if composite index is missing or orderBy fails
        devLog('Sales history ordered query failed, retrying without orderBy', orderedError);
        const fallbackQuery = query(
          collection(db, 'sales'),
          where('userId', '==', currentUser.uid)
        );
        snapshot = await getDocs(fallbackQuery);
      }

      const salesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate?.() || doc.data().createdAt?.toDate?.() || null
      }));

      const sorted = salesData.sort((a, b) => {
        const aTime = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
        const bTime = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
        return bTime - aTime;
      });

      setSalesHistory(sorted);
    } catch (error) {
      devError('Error loading sales history:', error);
      setError('Failed to load sales history. Please try again.');
      setSalesHistory([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add a new sales entry row
   */
  const addSalesEntry = () => {
    setNewSubmission(prev => ({
      ...prev,
      salesEntries: [
        ...prev.salesEntries,
        { category: '', itemName: '', amount: '' }
      ]
    }));
  };

  /**
   * Remove a sales entry row
   */
  const removeSalesEntry = (index) => {
    if (newSubmission.salesEntries.length > 1) {
      setNewSubmission(prev => ({
        ...prev,
        salesEntries: prev.salesEntries.filter((_, i) => i !== index)
      }));
    }
  };

  /**
   * Update a specific sales entry field
   */
  const updateSalesEntry = (index, field, value) => {
    setNewSubmission(prev => ({
      ...prev,
      salesEntries: prev.salesEntries.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      )
    }));
  };

  /**
   * Calculate total amount from all entries
   */
  const calculateTotal = () => {
    return newSubmission.salesEntries.reduce((total, entry) => {
      const amount = parseFloat(entry.amount) || 0;
      return total + amount;
    }, 0);
  };

  /**
   * Validate form before submission
   */
  const validateForm = () => {
    const errors = [];

    if (newSubmission.salesEntries.length === 0) {
      errors.push('At least one sale entry is required');
    }

    newSubmission.salesEntries.forEach((entry, index) => {
      if (!entry.category) {
        errors.push(`Entry ${index + 1}: Category is required`);
      }
      if (!entry.itemName || entry.itemName.trim() === '') {
        errors.push(`Entry ${index + 1}: Item name is required`);
      }
      if (!entry.amount || parseFloat(entry.amount) <= 0) {
        errors.push(`Entry ${index + 1}: Valid amount is required`);
      }
    });

    return errors;
  };

  /**
   * Submit sales record to Firestore
   */
  const handleSubmit = async () => {
    try {
      setError('');
      setSuccess('');

      // Validate form
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        setError(validationErrors.join('. '));
        return;
      }

      setLoading(true);

      // Prepare submission data
      const salesData = {
        userId: currentUser.uid,
        userName: userProfile.displayName || 'Unknown',
        userEmail: userProfile.email || currentUser.email,
        programName: TAKE_A_LOT_PROGRAM.name,
        programCode: TAKE_A_LOT_PROGRAM.code,
        cohortId: userProfile.cohortId || '',
        cohortName: userProfile.cohortName || '',

        salesEntries: newSubmission.salesEntries.map(entry => ({
          category: entry.category,
          itemName: entry.itemName.trim(),
          amount: parseFloat(entry.amount)
        })),

        totalAmount: calculateTotal(),
        entryCount: newSubmission.salesEntries.length,
        notes: newSubmission.notes.trim(),

        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      // Save to Firestore
      await addDoc(collection(db, 'sales'), salesData);

      // Success handling
      setSuccess('Sales record submitted successfully!');
      setShowModal(false);

      // Reset form
      setNewSubmission({
        salesEntries: [{ category: '', itemName: '', amount: '' }],
        notes: ''
      });

      // Reload sales history
      await loadSalesHistory();

    } catch (error) {
      devError('Error submitting sales:', error);
      setError('Failed to submit sales record. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open submission modal
   */
  const handleOpenModal = () => {
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  /**
   * Close modal and reset form
   */
  const handleCloseModal = () => {
    setShowModal(false);
    setNewSubmission({
      salesEntries: [{ category: '', itemName: '', amount: '' }],
      notes: ''
    });
    setError('');
  };

  return (
    <Container className="mt-4">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Take A Lot Sales Recording</h2>
          <p className="text-muted mb-0">Record your sales transactions for the Take A Lot training programme</p>
        </div>
        <Button variant="primary" size="lg" onClick={handleOpenModal}>
          + Record New Sales
        </Button>
      </div>

      {/* Success/Failure Alerts */}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Sales History Card */}
      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">My Sales History</h5>
          <Button
            variant="light"
            size="sm"
            onClick={loadSalesHistory}
            disabled={loading}
          >
            Refresh
          </Button>
        </Card.Header>
        <Card.Body>
          {loading && salesHistory.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading sales history...</p>
            </div>
          ) : salesHistory.length === 0 ? (
            <Alert variant="info">
              <strong>No sales records found.</strong> If you already captured sales, refresh — data may still be syncing.
            </Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Total Amount</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {salesHistory.map(sale => (
                  <tr key={sale.id}>
                    <td>
                      {sale.submittedAt?.toLocaleDateString('en-ZA', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td>
                      <Badge bg="secondary">{sale.entryCount} items</Badge>
                    </td>
                    <td>
                      <strong>R {sale.totalAmount?.toFixed(2)}</strong>
                    </td>
                    <td>
                      <details>
                        <summary style={{ cursor: 'pointer' }}>View items</summary>
                        <ul className="mt-2 mb-0">
                          {sale.salesEntries?.map((entry, idx) => (
                            <li key={idx}>
                              <strong>{entry.category}:</strong> {entry.itemName} - R{entry.amount?.toFixed(2)}
                            </li>
                          ))}
                        </ul>
                        {sale.notes && (
                          <div className="mt-2">
                            <small><strong>Notes:</strong> {sale.notes}</small>
                          </div>
                        )}
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Sales Leaderboard */}
      <TakeALotLeaderboard />

      {/* Submission Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Sales Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Form>
            {/* Sales Entries Section */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Sales Entries</h6>
                <Badge bg="info">{newSubmission.salesEntries.length} items</Badge>
              </div>

              {newSubmission.salesEntries.map((entry, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Category <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={entry.category}
                            onChange={(e) => updateSalesEntry(index, 'category', e.target.value)}
                            required
                          >
                            <option value="">Select category...</option>
                            {SALES_CATEGORIES.map(category => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Item Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Samsung TV 55 inch"
                            value={entry.itemName}
                            onChange={(e) => updateSalesEntry(index, 'itemName', e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Amount (R) <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={entry.amount}
                            onChange={(e) => updateSalesEntry(index, 'amount', e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1} className="d-flex align-items-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeSalesEntry(index)}
                          disabled={newSubmission.salesEntries.length === 1}
                          title="Remove entry"
                          style={{ marginTop: '8px' }}
                        >
                          ✕
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}

              <Button
                variant="outline-success"
                onClick={addSalesEntry}
                className="w-100"
              >
                + Add Another Sale Entry
              </Button>
            </div>

            {/* Total Display */}
            <Card className="mb-3 bg-light">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Total Amount:</h5>
                  <h4 className="mb-0 text-success">
                    R {calculateTotal().toFixed(2)}
                  </h4>
                </div>
              </Card.Body>
            </Card>

            {/* Notes Field */}
            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add any additional notes about this sales transaction..."
                value={newSubmission.notes}
                onChange={(e) => setNewSubmission({ ...newSubmission, notes: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              'Submit Sales Record'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TakeALotSales;
